import { useQuery, useQueryClient } from '@tanstack/react-query'

import { toProjectSummary } from '@/shared/api/adapt'
import { api } from '@/shared/api/client'
import type { Page, ProjectListItem } from '@/shared/api/contracts'
import { qk } from '@/shared/api/query-keys'
import { ensureUserIndex } from '@/shared/hooks/use-users'
import { toSearchParams } from '@/shared/lib/query-params'
import type { PortfolioKpis, ProjectSummary } from '@/shared/types/domain'

import { PARAM, type QueryFilters } from './use-portfolio-filters'

/**
 * Los filtros de la URL están en castellano y son de PANTALLA; el backend tiene
 * los suyos. Acá se traducen, y **todo** se filtra en el servidor:
 *
 * - `empresa` (repetible)     -> `company_ids`
 * - `responsable` (repetible) -> `owner_ids`, y `sin-asignar` -> `include_unassigned`.
 *   Antes "sin responsable" se filtraba en el cliente sobre la página cargada:
 *   con más de 200 proyectos la lista quedaba corta sin decirlo.
 * - `fase` (repetible)        -> `stage_ids`. El backend los expande por `key` a la
 *   columna equivalente de cada empresa: el desplegable ofrece las columnas del
 *   set del sistema y cada proyecto vive en el clon de la suya (mismo `key`,
 *   otro id). Antes viajaba la **categoría**, que no distingue dos columnas
 *   activas distintas ("En desarrollo" y "En QA").
 * - `q`                       -> `search`
 * - `ongoing`                 -> `include_ongoing`, **siempre explícito**: el default
 *   del backend es `false`, así que omitirlo cuando la pantalla dice "incluir
 *   ongoing" era pedir justo lo contrario de lo que el control muestra.
 * - `include_done` sólo lo pide el tablero, que tiene columna de culminados. Con
 *   un filtro de fase no hace falta: elegir columnas es pedir esas columnas,
 *   "Culminado" incluido, y eso lo resuelve el servidor.
 */
export const UNASSIGNED = 'sin-asignar'

function lista(valor: string | string[] | undefined): string[] {
  if (valor === undefined) return []
  return Array.isArray(valor) ? valor : [valor]
}

/** Empresa y responsable, igual para la lista y para los KPIs. */
function filtrosComunes(filters: QueryFilters) {
  const owners = lista(filters[PARAM.owner])
  return {
    company_ids: lista(filters[PARAM.company]),
    owner_ids: owners.filter((owner) => owner !== UNASSIGNED),
    include_unassigned: owners.includes(UNASSIGNED) || undefined,
  }
}

/** Traduce los filtros de pantalla a la query del backend. */
export function toPortfolioQuery(
  filters: QueryFilters,
  { includeDone = false }: { includeDone?: boolean } = {},
): string {
  const search = filters[PARAM.search]
  return toSearchParams({
    ...filtrosComunes(filters),
    stage_ids: lista(filters[PARAM.stage]),
    search: typeof search === 'string' ? search : undefined,
    include_ongoing: filters[PARAM.ongoing] !== '0',
    // Buscar por nombre es querer encontrar ESE proyecto, esté en la fase que
    // esté. Sin esto "Drive to Dropbox" no aparecía: está culminado, y la lista
    // del día los deja afuera.
    include_done: includeDone || (typeof search === 'string' && search.trim() !== ''),
    limit: 200,
  })
}

/**
 * La página de proyectos del portafolio.
 *
 * La ruta es `projects/portfolio`, no `portfolio/projects`, y devuelve `Page`,
 * no un array. Viene YA ordenada por rank con el ordinal `position` calculado
 * por el servidor.
 */
export interface PortfolioProjectsOptions {
  /**
   * La vista `tablero` necesita los culminados SIEMPRE.
   *
   * El tablero tiene una columna "Culminado" y el default de `include_done` es
   * `false`, así que sin esto la columna diría "Nada acá" para siempre — que es
   * exactamente el bug que el QA funcional levantó como 1.2 cuando el tablero
   * vivía en `/board` con su propia query. Al consolidarlo en el portafolio, la
   * condición se declara acá y no se vuelve a perder.
   */
  needsDone?: boolean
}

export function usePortfolioProjects(
  filters: QueryFilters,
  { needsDone = false }: PortfolioProjectsOptions = {},
) {
  const client = useQueryClient()

  return useQuery({
    queryKey: qk.portfolio.table({ ...filters, includeDone: needsDone }),
    queryFn: async (): Promise<ProjectSummary[]> => {
      const [users, page] = await Promise.all([
        ensureUserIndex(client),
        api.get<Page<ProjectListItem>>(
          `projects/portfolio${toPortfolioQuery(filters, { includeDone: needsDone })}`,
        ),
      ])
      return page.items.map((item) => toProjectSummary(item, users))
    },
  })
}

/**
 * Los cuatro KPIs de la cabecera.
 *
 * La ruta es **`GET /projects/portfolio/kpis`** (no `/portfolio/kpis`, que era
 * una invención del frontend y nunca existió), y los calcula el servidor
 * **sobre el portafolio entero que el principal puede ver**, no sobre la página
 * cargada. La diferencia no es de completitud sino de verdad: un KPI derivado
 * de la primera página no dice "faltan datos", dice un número que parece
 * cierto.
 *
 * Reciben empresa y responsable —los mismos que la lista—; la fase no, porque
 * los KPIs ya fijan su propio alcance ("ni culminados ni pausados"). El recorte
 * por empresa real lo hace RLS.
 */
export function usePortfolioKpis(filters: QueryFilters) {
  // Empresa y responsable viajan TAMBIÉN a los KPIs. Sin esto, en "solo lo mío"
  // la cabecera contaba el portafolio entero mientras la tabla de abajo
  // mostraba lo de una persona: dos números que contestan preguntas distintas a
  // treinta centímetros uno del otro (el mismo modo de falla que el QA 1.7).
  const comunes = filtrosComunes(filters)

  return useQuery({
    queryKey: qk.portfolio.kpis(comunes),
    queryFn: () => api.get<PortfolioKpis>(`projects/portfolio/kpis${toSearchParams(comunes)}`),
  })
}
